import type {ReactElement} from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import type {OverlayChildren} from 'react-bootstrap/Overlay';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {MiddleClickNewTab} from '@Components/middle-click-new-tab';
import type {SidebarTriggerProps} from './sidebar-trigger.types';
import styles from './sidebar-trigger.module.scss';

/**
 * One collapsed-sidebar action: a square, transparent icon button with a right-anchored
 * hover/focus tooltip. The four sidebar actions differ only in tooltip/icon/onClick (and
 * optionally an active-state colour or a new-tab href), so they all render through this.
 */
export function SidebarTrigger({tooltip, icon, onClick, iconClassName = 'radius-8', type = IconType.TRANSPARENT, flatIconType, href}: SidebarTriggerProps): ReactElement {
  const renderTooltip = (): OverlayChildren => {
    return (
      <div className={`spacing-p-t-1 spacing-p-b-1 spacing-p-l-2 spacing-p-r-2 radius-8 ${styles.tooltipContainer}`}>
        <Text size={TextSize.XSMALL} className={styles.tooltipText} val={tooltip} />
      </div>
    );
  };

  const renderIcon = (): ReactElement => {
    return (
      <Icon
        className={iconClassName}
        onClick={onClick}
        icon={icon}
        size={IconSize.SIZE_ICON_20}
        shape={IconShape.SQUARE}
        type={type}
        flatIconType={flatIconType}
        isUsedAsButton
      />
    );
  };

  const renderTarget = (): ReactElement => {
    if (href !== undefined) {
      return <MiddleClickNewTab href={href}>{renderIcon()}</MiddleClickNewTab>;
    }
    return renderIcon();
  };

  return (
    <OverlayTrigger
      defaultShow={false}
      trigger={['hover', 'focus']}
      overlay={renderTooltip()}
      delay={{show: 100, hide: 100}}
      placement="right"
      offset={[0, 8]}
    >
      {renderTarget()}
    </OverlayTrigger>
  );
}
